// Generated by tutorials/record.cjs from tutorials/articles/h20-admit-resident.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h20 --text-only` (wording) or
// `node tutorials/record.cjs h20` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'admit-resident',
  catalogId: 'h20',
  title: 'Admitting a new resident',
  summary: 'Add a new resident with their room, diet order and allergies, put them on the tray line, and check their first tray ticket before it prints.',
  audience: 'Dietary managers and anyone who enters new admissions',
  area: 'residents',
  learn: [
    'Add a resident in the New Resident window',
    'Enter the room, the diet types and the consistencies',
    'Record food allergies so the kitchen leaves those foods off the tray',
    'Put the resident on the tray line',
    'Check the first tray ticket in the Tray Ticket Visualizer',
  ],
  keywords: [
    'admit',
    'admission',
    'new resident',
    'add resident',
    'room',
    'bed',
    'diet order',
    'consistency',
    'allergy',
    'allergies',
    'tray line',
    'first tray',
  ],
  steps: [
    {
      id: 'open-new',
      part: 'Add the resident',
      heading: 'Open the New Resident window',
      text: 'In the menu, select Residents. Then select New at the top of the list.',
      media: {
        image: {
          src: '/help/admit-resident/open-new.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/admit-resident/open-new.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'The Residents page with the New button selected, and the New Resident window opening on its Basics step.',
      },
    },
    {
      id: 'basics',
      part: 'Add the resident',
      heading: 'Enter the basics',
      text: 'Type the first and last name, the resident ID and the birth date, and pick the gender. The admit date is today; change it if the resident arrived on another day.\n\nFields with a star must be filled in.',
      media: {
        image: {
          src: '/help/admit-resident/basics.webp',
          width: 944,
          height: 720,
        },
        clip: {
          src: '/help/admit-resident/basics.mp4',
          width: 944,
          height: 720,
          durationSec: 6,
        },
        alt: 'The Basics step filled in: admit date today, the name OPAL HENSHAW, resident ID 10434, birth date 03/14/1940 and gender Female.',
      },
    },
    {
      id: 'location',
      part: 'Add the resident',
      heading: 'Pick the unit, room and bed',
      text: 'Select Next. Pick the unit, then type the room and the bed. The room decides where the tray goes and how it is sorted on the tray line.',
      media: {
        image: {
          src: '/help/admit-resident/location.webp',
          width: 944,
          height: 720,
        },
        clip: {
          src: '/help/admit-resident/location.mp4',
          width: 944,
          height: 720,
          durationSec: 6,
        },
        alt: 'The Location step with Unit Willow, Room 140 and Bed B.',
      },
    },
    {
      id: 'diet',
      part: 'Add the resident',
      heading: 'Enter the diet order',
      text: 'Select Next. Under Diet Types, pick each diet in the order. Under Consistencies, pick the food texture first, then the liquid consistency.\n\nA check under In MC means your menu cycle has menus for it.',
      media: {
        image: {
          src: '/help/admit-resident/diet.webp',
          width: 944,
          height: 802,
        },
        clip: {
          src: '/help/admit-resident/diet.mp4',
          width: 944,
          height: 802,
          durationSec: 6,
        },
        alt: 'The Diet step with Consistent Carbohydrate under Diet Types, and Regular and Thin Liquids under Consistencies.',
      },
    },
    {
      id: 'review',
      part: 'Add the resident',
      heading: 'Check the review',
      text: 'Select Next. The Review step lists everything you entered. If something is wrong, select Back or the step\'s name at the top to change it.',
      media: {
        image: {
          src: '/help/admit-resident/review.webp',
          width: 944,
          height: 930,
        },
        clip: {
          src: '/help/admit-resident/review.mp4',
          width: 944,
          height: 930,
          durationSec: 3.5,
        },
        alt: 'The Review step listing the admit date, name, resident ID, birth date, gender, unit, room and bed, and the diet order.',
      },
    },
    {
      id: 'create',
      part: 'Add the resident',
      heading: 'Create the resident and open their page',
      text: 'Select Create and Open Profile. The resident is saved with their diet order, and their page opens so you can add the rest.\n\nCreate Resident saves them and keeps you on the Residents page.',
      media: {
        image: {
          src: '/help/admit-resident/create.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/admit-resident/create.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'The new resident\'s page: Henshaw, Opal, Active, Unit Willow, Room 140, Bed B, with the diet order under the name.',
      },
    },
    {
      id: 'allergies',
      part: 'Allergies and the tray line',
      heading: 'Add food allergies',
      text: 'On the left, select Food Allergies. Pick each allergy from the list, one per box. Type any drug allergies in Drug Allergies.\n\nThe allergies print on every tray ticket. When an allergy is linked to an adjustment, Dietech also keeps foods with that allergen off the tray when you save.',
      media: {
        image: {
          src: '/help/admit-resident/allergies.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/admit-resident/allergies.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Food Allergies section with Shellfish picked in Allergy 1.',
      },
    },
    {
      id: 'tray-line',
      part: 'Allergies and the tray line',
      heading: 'Put the resident on the tray line',
      text: 'Select Tray Line, then Assign Tray Line. Pick the set for breakfast. The resident is added at the end of that set; use the arrows to move them to their place.',
      media: {
        image: {
          src: '/help/admit-resident/tray-line.webp',
          width: 1280,
          height: 734,
        },
        clip: {
          src: '/help/admit-resident/tray-line.mp4',
          width: 1280,
          height: 734,
          durationSec: 5.3,
        },
        alt: 'The Tray Line Assignment window: Breakfast Set Willow Cart 3, with the new resident in the list of that cart\'s rooms.',
      },
    },
    {
      id: 'tray-line-copy',
      part: 'Allergies and the tray line',
      heading: 'Copy it to lunch and dinner',
      text: 'Next to Lunch Set, select the copy button, then Copy Set and Tray from Breakfast. Do the same for dinner. Then select Save.',
      media: {
        image: {
          src: '/help/admit-resident/tray-line-copy.webp',
          width: 1280,
          height: 734,
        },
        clip: {
          src: '/help/admit-resident/tray-line-copy.mp4',
          width: 1280,
          height: 734,
          durationSec: 6,
        },
        alt: 'The same window with the resident in the Willow Cart at breakfast, lunch and dinner.',
      },
    },
    {
      id: 'save',
      part: 'Allergies and the tray line',
      heading: 'Save the resident',
      text: 'Select Save in the window, then Save Changes at the top right of the page. You go back to the Residents page, where the new resident is selected.',
      media: {
        image: {
          src: '/help/admit-resident/save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/admit-resident/save.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Residents page with Henshaw, Opal in the list: Willow, room 140, bed B, Consistent Carbohydrate, Regular, Thin Liquids.',
      },
    },
    {
      id: 'first-tray',
      part: 'Allergies and the tray line',
      heading: 'Check the first tray',
      text: 'With the resident selected, select Visualizer. You see their tray ticket for the meal you pick: the diet order, the allergies in red, the set and tray, and each item in the right consistency.',
      media: {
        image: {
          src: '/help/admit-resident/first-tray.webp',
          width: 1280,
          height: 794,
        },
        clip: {
          src: '/help/admit-resident/first-tray.mp4',
          width: 1280,
          height: 794,
          durationSec: 2,
        },
        alt: 'The Tray Ticket Visualizer for Henshaw, Opal: Consistent Carbohydrate, Regular and Thin Liquids, ALLERGY: Shellfish, and the breakfast items.',
      },
    },
  ],
  tips: [
    'Press Ctrl and N on the Residents page to open the New Resident window.',
    'A check under In MC means your menu cycle has menus for that diet type or consistency. If it has no check, the menu has nothing written for it, so check with whoever plans your menus.',
    'Not sure of the diet order yet? You can create the resident and add the diet order later, but the resident shows No Diet on the Residents page until you do.',
  ],
  notes: [
    'An allergy in white is linked to an adjustment, so Dietech keeps foods with that allergen off the tray. An allergy in yellow is a reminder on the tray ticket only. Ask whoever manages your food allergy list to link it.',
    'A resident with no tray line shows No Tray Line on the Residents page, and their ticket prints with no set or tray number.',
  ],
  related: ['change-diet-order', 'allergies-likes-dislikes', 'tray-tickets'],
  recorded: '2026-10-25',
};

export default article;
